import { useState, useEffect, useCallback } from 'react';
import { 
  Globe, Sparkles, Video, Users, FileText, Settings, Plus,
  Eye, Edit, Trash2, Play, Pause, Download, Share2, Copy,
  Palette, Layout, ShoppingCart, Megaphone, Mail, MessageSquare,
  Image, Mic, Camera, Film, Wand2, RefreshCw, Loader2, Calculator,
  Check, X, ChevronRight, ExternalLink, BarChart3, Target, CreditCard,
  Zap, Clock, Calendar, Send, Heart, MessageCircle, Bookmark, Building2,
  HelpCircle, BookOpen, Lightbulb, ArrowRight, AlertCircle, Minus,
  ChevronDown, ChevronUp, Shield, Phone, Plug, Brain, Leaf, Wine, Tractor
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import api from '@/lib/api';

// ID société Frédéric Développement (seule autorisée pour vente logiciel)
const SOCIETE_FREDERIC_DEV_ID = "729fbf20-eccd-4bde-9ef3-e5a03a34a27f";

// Icônes pour les briques
const BRIQUE_ICONS = {
  users: Users,
  building: Building2,
  wine: Wine,
  cow: Users,
  flower: Sparkles,
  tractor: Tractor,
  carrot: Leaf,
  globe: Globe,
  video: Video,
  shield: Shield,
  leaf: Leaf,
  phone: Phone,
  plug: Plug,
  brain: Brain
};

// Composant Brique Option
const BriqueOption = ({ brique, selected, onToggle, quantity, onQuantityChange }) => {
  const Icon = BRIQUE_ICONS[brique.icon] || Sparkles;
  const isQuantifiable = brique.id === 'collaborateur_sup' || brique.id === 'workspace_sup';
  
  return (
    <div 
      className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
        selected 
          ? 'border-primary bg-primary/5 shadow-md' 
          : 'border-border hover:border-primary/50 hover:bg-muted/50'
      }`}
      onClick={() => !isQuantifiable && onToggle(brique.id)}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${selected ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold truncate">{brique.nom}</h4>
            <span className="text-lg font-bold text-primary">+{brique.prix_unitaire}€</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{brique.description}</p>
          <p className="text-xs text-muted-foreground">{brique.unite}</p>
          
          {isQuantifiable && (
            <div className="flex items-center gap-2 mt-3" onClick={e => e.stopPropagation()}>
              <Button 
                variant="outline" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => onQuantityChange(brique.id, Math.max(0, (quantity || 0) - 1))}
              >
                <Minus className="w-4 h-4" />
              </Button>
              <span className="w-12 text-center font-semibold">{quantity || 0}</span>
              <Button 
                variant="outline" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => onQuantityChange(brique.id, (quantity || 0) + 1)}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          )}
          
          {!isQuantifiable && (
            <div className="mt-2">
              <Checkbox 
                checked={selected} 
                onCheckedChange={() => onToggle(brique.id)}
                onClick={e => e.stopPropagation()}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Composant Calculateur de Prix
const PriceCalculator = ({ config, calcul, onConfigChange, onCheckout, loading }) => {
  return (
    <Card className="sticky top-4 border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-background">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-primary" />
            Votre Abonnement
          </CardTitle>
          <Badge variant="secondary" className="text-xs">Conversion Agri</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Détails du calcul */}
        <div className="space-y-2">
          {calcul?.details?.map((item, i) => (
            <div key={i} className={`flex justify-between text-sm ${item.economie ? 'text-green-600 font-semibold' : ''}`}>
              <span>{item.item}</span>
              <span>{item.economie ? '' : '+'}{item.prix.toFixed(2)}€</span>
            </div>
          ))}
        </div>
        
        <div className="border-t pt-3">
          {calcul?.plafond_applique && (
            <div className="flex items-center gap-2 text-green-600 text-sm mb-2">
              <Check className="w-4 h-4" />
              <span>Pack Complet appliqué ! Vous économisez {calcul.economie.toFixed(2)}€</span>
            </div>
          )}
          
          <div className="flex justify-between items-center">
            <span className="text-lg font-semibold">Total mensuel</span>
            <div className="text-right">
              {calcul?.plafond_applique && (
                <span className="text-sm text-muted-foreground line-through mr-2">
                  {calcul.prix_calcule.toFixed(2)}€
                </span>
              )}
              <span className="text-3xl font-bold text-primary">{calcul?.prix_final?.toFixed(2)}€</span>
              <span className="text-muted-foreground">/mois</span>
            </div>
          </div>
        </div>
        
        <Button 
          className="w-full h-12 text-lg bg-gradient-to-r from-primary to-green-600 hover:from-primary/90 hover:to-green-600/90"
          onClick={onCheckout}
          disabled={loading}
          data-testid="checkout-btn"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
          ) : (
            <CreditCard className="w-5 h-5 mr-2" />
          )}
          Souscrire maintenant
        </Button>
        
        <p className="text-xs text-center text-muted-foreground">
          Paiement sécurisé par Stripe • Facture au nom de Conversion Agri
        </p>
      </CardContent>
    </Card>
  );
};

// Page principale
export default function VenteEtSitesPage() {
  const [activeTab, setActiveTab] = useState('sites');
  const [loading, setLoading] = useState(false);
  const { activeSociete } = useWorkspace();
  
  // Vérifier si c'est Frédéric Développement (autorisé pour vente logiciel)
  const isConversionAgri = activeSociete?.id === SOCIETE_FREDERIC_DEV_ID;
  
  // Configuration tarifs
  const [siteConfig, setSiteConfig] = useState(null);
  const [briques, setBriques] = useState([]);
  const [planBase, setPlanBase] = useState(null);
  const [packComplet, setPackComplet] = useState(null);
  
  // Configuration utilisateur
  const [config, setConfig] = useState({
    collaborateurs: 1,
    workspaces: 1,
    modulesSelectionnes: []
  });
  
  // Calcul dynamique
  const [calcul, setCalcul] = useState(null);
  
  // Sites
  const [sites, setSites] = useState([]);
  const [showCreateSite, setShowCreateSite] = useState(false);
  const [newSite, setNewSite] = useState({
    nom_site: '',
    type_site: 'vitrine',
    use_erp_data: true,
    couleur_principale: '#22c55e',
    slogan: ''
  });
  
  // Vidéos & Influenceurs
  const [influenceurs, setInfluenceurs] = useState([]);
  const [videos, setVideos] = useState([]);
  const [showGenerateVideo, setShowGenerateVideo] = useState(false);
  const [selectedInfluenceur, setSelectedInfluenceur] = useState(null);
  const [videoRequest, setVideoRequest] = useState({
    type_video: 'reel',
    sujet: '',
    duree_cible: 60
  });
  const [generating, setGenerating] = useState(false);

  // CRM
  const [prospects, setProspects] = useState([]);
  const [statsVente, setStatsVente] = useState(null);
  const [facturationConfig, setFacturationConfig] = useState(null);

  useEffect(() => {
    fetchTarifs();
    fetchSites();
    fetchInfluenceurs();
    fetchVideos();
    fetchCRMStats();
    fetchFacturation();
  }, []);

  useEffect(() => {
    // Recalculer le prix à chaque changement de config
    if (planBase) {
      recalculerPrix();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, planBase]);

  const fetchTarifs = async () => {
    try {
      const [configRes, briquesRes] = await Promise.all([
        api.get('/vente-auto/site/config'),
        api.get('/vente-auto/tarifs/briques')
      ]);
      
      setSiteConfig(configRes.data);
      setBriques(briquesRes.data.briques || []);
      setPlanBase(briquesRes.data.plan_base);
      setPackComplet(briquesRes.data.pack_complet);
    } catch (err) {
      console.error('Erreur chargement tarifs:', err);
    }
  };

  const recalculerPrix = async () => {
    try {
      const modules = config.modulesSelectionnes.join(',');
      const res = await api.get(`/vente-auto/tarifs/calculer?collaborateurs=${config.collaborateurs}&workspaces=${config.workspaces}&modules=${modules}`);
      setCalcul(res.data);
    } catch (err) {
      console.error('Erreur calcul prix:', err);
    }
  };

  const fetchSites = async () => {
    try {
      const res = await api.get('/studio-ia/sites');
      setSites(res.data.sites || []);
    } catch (err) {
      console.log('Erreur sites:', err);
    }
  };

  const fetchInfluenceurs = async () => {
    try {
      const res = await api.get('/studio-ia/influenceurs');
      setInfluenceurs(res.data.predefinis || []);
    } catch (err) {
      console.log('Erreur influenceurs:', err);
    }
  };

  const fetchVideos = async () => {
    try {
      const res = await api.get('/studio-ia/videos');
      setVideos(res.data.videos || []);
    } catch (err) {
      console.log('Erreur videos:', err);
    }
  };

  const fetchCRMStats = async () => {
    try {
      const res = await api.get('/vente-auto/stats/dashboard');
      setStatsVente(res.data);
    } catch (err) {
      console.log('Erreur stats CRM:', err);
    }
  };

  const fetchFacturation = async () => {
    try {
      const res = await api.get('/vente-auto/facturation/config');
      setFacturationConfig(res.data);
    } catch (err) {
      console.log('Config facturation non disponible');
    }
  };

  const handleToggleModule = (moduleId) => {
    setConfig(prev => ({
      ...prev,
      modulesSelectionnes: prev.modulesSelectionnes.includes(moduleId)
        ? prev.modulesSelectionnes.filter(m => m !== moduleId)
        : [...prev.modulesSelectionnes, moduleId]
    }));
  };

  const handleQuantityChange = (type, value) => {
    if (type === 'collaborateur_sup') {
      setConfig(prev => ({ ...prev, collaborateurs: value + 1 })); // +1 car le premier est inclus
    } else if (type === 'workspace_sup') {
      setConfig(prev => ({ ...prev, workspaces: value + 1 }));
    }
  };

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const res = await api.post('/vente-auto/paiement/checkout', {
        collaborateurs: config.collaborateurs,
        workspaces: config.workspaces,
        modules_selectionnes: config.modulesSelectionnes,
        origin_url: window.location.origin
      });
      
      // Rediriger vers Stripe
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la création du paiement');
      setLoading(false);
    }
  };

  const handleCreateSite = async () => {
    if (!newSite.nom_site) {
      toast.error('Nom du site requis');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/studio-ia/sites/create', newSite);
      toast.success('Site créé avec succès !');
      setShowCreateSite(false);
      fetchSites();
      setNewSite({
        nom_site: '',
        type_site: 'vitrine',
        use_erp_data: true,
        couleur_principale: '#22c55e',
        slogan: ''
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur création site');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateVideo = async () => {
    if (!selectedInfluenceur) {
      toast.error('Sélectionnez un influenceur');
      return;
    }
    if (!videoRequest.sujet) {
      toast.error('Décrivez le sujet de la vidéo');
      return;
    }
    
    setGenerating(true);
    try {
      const res = await api.post('/studio-ia/video/generate', {
        influenceur_id: selectedInfluenceur.id,
        ...videoRequest
      });
      
      toast.success('Génération vidéo lancée !');
      setShowGenerateVideo(false);
      fetchVideos();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur génération vidéo');
    } finally {
      setGenerating(false);
    }
  };

  // Séparer les briques par catégorie
  const briquesCollaborateurs = briques.filter(b => b.id === 'collaborateur_sup' || b.id === 'workspace_sup');
  const briquesModules = briques.filter(b => b.id.startsWith('module_'));
  const briquesOptions = briques.filter(b => !b.id.startsWith('module_') && b.id !== 'collaborateur_sup' && b.id !== 'workspace_sup');

  return (
    <div className="p-6 space-y-6" data-testid="vente-sites-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          {isConversionAgri && (
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">
                Conversion Agri
              </Badge>
            </div>
          )}
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            {isConversionAgri ? 'Vente & Sites IA' : 'Sites & Marketing IA'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isConversionAgri 
              ? 'Commercialisation Bureau Agricole • Sites web • Influenceurs IA'
              : 'Créez vos sites web et contenus marketing avec l\'IA'
            }
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowGenerateVideo(true)}>
            <Video className="w-4 h-4 mr-2" />
            Créer vidéo
          </Button>
          <Button onClick={() => setShowCreateSite(true)} className="bg-gradient-to-r from-green-500 to-emerald-600">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau site
          </Button>
        </div>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Stats Conversion Agri uniquement */}
        {isConversionAgri && statsVente && (
          <>
            <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{statsVente.statistiques?.prospects_total || 0}</p>
                  <p className="text-sm text-muted-foreground">Prospects logiciel</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 bg-green-500 rounded-lg">
                  <Check className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{statsVente.statistiques?.par_statut?.client_actif || 0}</p>
                  <p className="text-sm text-muted-foreground">Clients actifs</p>
                </div>
              </CardContent>
            </Card>
          </>
        )}
        
        {/* Stats communes à tous */}
        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-purple-500 rounded-lg">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold">{sites.length}</p>
              <p className="text-sm text-muted-foreground">Mes sites</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-950/20 dark:to-red-950/20">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-orange-500 rounded-lg">
              <Video className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold">{videos.length}</p>
              <p className="text-sm text-muted-foreground">Vidéos IA</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`grid w-full ${isConversionAgri ? 'grid-cols-5' : 'grid-cols-2'}`}>
          <TabsTrigger value="sites" className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            <span className="hidden sm:inline">Sites</span>
          </TabsTrigger>
          <TabsTrigger value="videos" className="flex items-center gap-2">
            <Video className="w-4 h-4" />
            <span className="hidden sm:inline">Vidéos</span>
          </TabsTrigger>
          {isConversionAgri && (
            <>
              <TabsTrigger value="tarifs" className="flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                <span className="hidden sm:inline">Tarifs</span>
              </TabsTrigger>
              <TabsTrigger value="crm" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">CRM Logiciel</span>
              </TabsTrigger>
              <TabsTrigger value="stats" className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                <span className="hidden sm:inline">Stats</span>
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* Tab Tarifs */}
        <TabsContent value="tarifs" className="space-y-6">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Colonne configuration */}
            <div className="lg:col-span-2 space-y-6">
              {/* Plan de base */}
              {planBase && (
                <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-200">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-2xl">{planBase.nom}</CardTitle>
                        <CardDescription>{planBase.description}</CardDescription>
                      </div>
                      <div className="text-right">
                        <p className="text-4xl font-bold text-green-600">{planBase.prix_mensuel}€</p>
                        <p className="text-sm text-muted-foreground">/mois</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {planBase.features?.map((f, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <Check className="w-4 h-4 text-green-600" />
                          <span>{f}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Collaborateurs & Workspaces */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Équipe & Workspaces
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {briquesCollaborateurs.map(brique => (
                    <BriqueOption
                      key={brique.id}
                      brique={brique}
                      selected={
                        brique.id === 'collaborateur_sup' 
                          ? config.collaborateurs > 1 
                          : config.workspaces > 1
                      }
                      quantity={
                        brique.id === 'collaborateur_sup' 
                          ? config.collaborateurs - 1 
                          : config.workspaces - 1
                      }
                      onToggle={() => {}}
                      onQuantityChange={handleQuantityChange}
                    />
                  ))}
                </div>
              </div>

              {/* Modules Métiers */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Modules Métiers
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {briquesModules.map(brique => (
                    <BriqueOption
                      key={brique.id}
                      brique={brique}
                      selected={config.modulesSelectionnes.includes(brique.id)}
                      onToggle={handleToggleModule}
                      onQuantityChange={() => {}}
                    />
                  ))}
                </div>
              </div>

              {/* Options Avancées */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Options Avancées
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {briquesOptions.map(brique => (
                    <BriqueOption
                      key={brique.id}
                      brique={brique}
                      selected={config.modulesSelectionnes.includes(brique.id)}
                      onToggle={handleToggleModule}
                      onQuantityChange={() => {}}
                    />
                  ))}
                </div>
              </div>

              {/* Pack Complet */}
              {packComplet && calcul?.plafond_applique && (
                <Card className="bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-950/30 dark:to-pink-950/30 border-purple-300">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
                        <Sparkles className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold">🎉 Pack Complet activé !</h3>
                        <p className="text-muted-foreground">
                          Vous avez atteint le plafond. Profitez de toutes les fonctionnalités pour {packComplet.prix_mensuel}€/mois
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-green-600 font-semibold">
                          Économie de {calcul.economie.toFixed(2)}€/mois
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Colonne calculateur */}
            <div>
              <PriceCalculator
                config={config}
                calcul={calcul}
                onConfigChange={setConfig}
                onCheckout={handleCheckout}
                loading={loading}
              />
            </div>
          </div>
        </TabsContent>

        {/* Tab Sites */}
        <TabsContent value="sites" className="space-y-6">
          {sites.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <Globe className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">Aucun site créé</h3>
                <p className="text-muted-foreground mb-4">
                  Créez votre premier site web en quelques clics avec l'IA
                </p>
                <Button onClick={() => setShowCreateSite(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Créer mon premier site
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sites.map((site) => (
                <Card key={site.id} className="overflow-hidden">
                  <div className="aspect-video bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                    <Globe className="w-12 h-12 text-primary/50" />
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold">{site.nom}</h3>
                        <p className="text-sm text-muted-foreground">{site.url_preview}</p>
                      </div>
                      <Badge variant={site.publie ? 'default' : 'secondary'}>
                        {site.publie ? 'En ligne' : 'Brouillon'}
                      </Badge>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button variant="outline" size="sm" className="flex-1">
                        <Eye className="w-4 h-4 mr-1" />
                        Aperçu
                      </Button>
                      <Button variant="outline" size="sm" className="flex-1">
                        <Edit className="w-4 h-4 mr-1" />
                        Modifier
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tab Vidéos */}
        <TabsContent value="videos" className="space-y-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold">Vidéos Marketing IA</h2>
              <p className="text-sm text-muted-foreground">Générez des vidéos avec vos influenceurs virtuels</p>
            </div>
            <Button onClick={() => setShowGenerateVideo(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle vidéo
            </Button>
          </div>
          
          {videos.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <Video className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">Aucune vidéo créée</h3>
                <p className="text-muted-foreground mb-4">
                  Créez des vidéos promotionnelles avec l'IA
                </p>
                <Button onClick={() => setShowGenerateVideo(true)}>
                  <Film className="w-4 h-4 mr-2" />
                  Créer ma première vidéo
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {videos.map((video) => (
                <Card key={video.id} className="overflow-hidden">
                  <div className="aspect-video bg-gradient-to-br from-pink-100 to-purple-100 dark:from-pink-950/20 dark:to-purple-950/20 flex items-center justify-center">
                    <Film className="w-12 h-12 text-pink-500" />
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold truncate">{video.sujet || 'Vidéo IA'}</h3>
                    <p className="text-sm text-muted-foreground">{video.type_video} - {video.duree_cible}s</p>
                    <Badge className="mt-2" variant={video.statut === 'termine' ? 'default' : 'secondary'}>
                      {video.statut}
                    </Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tab CRM */}
        <TabsContent value="crm" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>CRM Automatique</CardTitle>
              <CardDescription>Suivi automatisé des prospects et clients</CardDescription>
            </CardHeader>
            <CardContent>
              {statsVente?.statuts_crm && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {statsVente.statuts_crm.map(statut => (
                    <div key={statut.id} className="text-center p-4 bg-muted/50 rounded-lg">
                      <p className="text-2xl font-bold">{statsVente.statistiques?.par_statut?.[statut.id] || 0}</p>
                      <p className="text-sm text-muted-foreground">{statut.nom}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Stats */}
        <TabsContent value="stats" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Conversions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span>Inscriptions ce mois</span>
                    <span className="font-bold">{statsVente?.statistiques?.inscriptions_mois || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Conversions</span>
                    <span className="font-bold">{statsVente?.statistiques?.conversions_mois || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Taux de conversion</span>
                    <span className="font-bold text-green-600">{statsVente?.statistiques?.taux_conversion || 0}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Revenus</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <p className="text-4xl font-bold text-green-600">
                    {statsVente?.statistiques?.revenus_mensuels_estimes || 0}€
                  </p>
                  <p className="text-muted-foreground">Revenus mensuels estimés</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog Création Site */}
      <Dialog open={showCreateSite} onOpenChange={setShowCreateSite}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Créer un nouveau site</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nom du site *</Label>
              <Input
                value={newSite.nom_site}
                onChange={(e) => setNewSite({...newSite, nom_site: e.target.value})}
                placeholder="Ex: Mon Exploitation"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Type de site</Label>
              <Select 
                value={newSite.type_site}
                onValueChange={(v) => setNewSite({...newSite, type_site: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vitrine">Site Vitrine</SelectItem>
                  <SelectItem value="ecommerce">E-commerce</SelectItem>
                  <SelectItem value="landing">Landing Page</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Slogan (optionnel)</Label>
              <Input
                value={newSite.slogan}
                onChange={(e) => setNewSite({...newSite, slogan: e.target.value})}
                placeholder="Ex: Le goût du terroir"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div>
                <Label>Utiliser les données ERP</Label>
                <p className="text-xs text-muted-foreground">Importer vos produits automatiquement</p>
              </div>
              <Switch
                checked={newSite.use_erp_data}
                onCheckedChange={(v) => setNewSite({...newSite, use_erp_data: v})}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateSite(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateSite} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Génération Vidéo */}
      <Dialog open={showGenerateVideo} onOpenChange={setShowGenerateVideo}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Créer une vidéo IA</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Influenceur IA</Label>
              <div className="grid grid-cols-2 gap-2">
                {influenceurs.slice(0, 4).map((inf) => (
                  <div 
                    key={inf.id}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedInfluenceur?.id === inf.id 
                        ? 'bg-primary/10 border-2 border-primary' 
                        : 'bg-muted/50 hover:bg-muted border-2 border-transparent'
                    }`}
                    onClick={() => setSelectedInfluenceur(inf)}
                  >
                    <p className="font-medium text-sm truncate">{inf.nom}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select 
                  value={videoRequest.type_video}
                  onValueChange={(v) => setVideoRequest({...videoRequest, type_video: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reel">Réel</SelectItem>
                    <SelectItem value="presentation">Présentation</SelectItem>
                    <SelectItem value="temoignage">Témoignage</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Durée</Label>
                <Select 
                  value={videoRequest.duree_cible.toString()}
                  onValueChange={(v) => setVideoRequest({...videoRequest, duree_cible: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30s</SelectItem>
                    <SelectItem value="60">1min</SelectItem>
                    <SelectItem value="120">2min</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Sujet</Label>
              <Textarea
                value={videoRequest.sujet}
                onChange={(e) => setVideoRequest({...videoRequest, sujet: e.target.value})}
                placeholder="Décrivez le sujet de la vidéo..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateVideo(false)}>
              Annuler
            </Button>
            <Button onClick={handleGenerateVideo} disabled={generating}>
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
              Générer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
